import {
    Button,
    Input,
    Message,
    Modal,
    Table, TableBody, TableCell,
    TableHeader,
    TableRow
} from "semantic-ui-react";
import * as React from "react";
import LabeledTextArea from "../../../components/labeled-textarea/labeled-textarea";
import "./compensation-new.css"
import {
    Form, Link, Params,
    redirect,
    useActionData, useLoaderData, useNavigate,
    useSubmit
} from "react-router-dom";
import { RootStore } from "../../../stores/root-store";
import { CompensationDtoFactory } from "../../../stores/compensation/compensation-store";
import { TransportError } from "../../../stores/data-transporter";
import { CreationProject, UsageProject } from "../../../stores/project/project";
import { getInt } from "../../../globals/form-data";
import LabeledValue from "../../../components/labeled-value/labeled-value";
import LabeledInput from "../../../components/labeled-input/labeled-input";
import { Constants } from "../../../globals/constants";
import { getLabel } from "../../../reflection/reflection";

type LoadResult = {
    usageProject: UsageProject;
    creationProject: CreationProject;
}

export async function loader(
    params: Params,
    rootStore: RootStore
) {
    if (params.projectId !== undefined && params.creationProjectId !== undefined) {
        const usageProjectId = parseInt(params.projectId) || 0;
        const creationProjectId = parseInt(params.creationProjectId) || 0;
        const rootStoreSession = rootStore.newSession();
        await rootStoreSession.municipalityStore.loadAll();
        const usageProject = await rootStoreSession.projectStore.loadUsageProject(usageProjectId);
        await rootStoreSession.areaStore.loadUsageAreas({ projectId: usageProjectId });
        await rootStoreSession.compensationStore.loadWithOptions({ usageProjectId: usageProjectId });
        const creationProject = await rootStoreSession.projectStore.loadCreationProject(creationProjectId);
        await rootStoreSession.areaStore.loadCreationAreas({ projectId: creationProjectId });
        return { usageProject, creationProject };
    }

    throw new Response("", {
        status: 404,
        statusText: "Beanspruchungs- oder Neuschaffungsprojekt wurde nicht gefunden",
    });
}

export async function action(request: Request, rootStore: RootStore) {
    const formData = await request.formData();
    const compensationDto = CompensationDtoFactory.makeCompensationDto(formData);

    if (compensationDto.area !== null) {
        const maxArea = getInt(formData, "maxArea");

        if (isNaN(compensationDto.area)) {
            return "Die Kompensationsfläche muss ausgefüllt sein.";
        }

        if (compensationDto.area < 0) {
            return "Die Kompensationsfläche muss positiv sein."
        }

        if (compensationDto.area > maxArea) {
            return `Die Kompensationsfläche muss kleiner oder gleich der maximal verfügbaren Fläche ${maxArea.toLocaleString()} sein.`;
        }

        if (Math.ceil(compensationDto.area) !== compensationDto.area) {
            return `Die Kompensationsfläche muss ganzzahlig sein.`;
        }
    }

    try {
        const rootStoreSession = rootStore.session;
        const compensation = await rootStoreSession.compensationStore.add(compensationDto);
        return redirect(`/projekte/${Constants.projectCategoryUsage}/${compensation.usageProjectId}`);
    } catch (e: TransportError) {
        switch (e.statusCode) {
            case 400:
                return "Es sind nicht alle erforderlichen Felder ausgefüllt.";
            default:
                return "Es ist ein unerwarteter Fehler aufgetreten!";
        }
    }
}

export default function CompensationNew(): JSX.Element {
    const { usageProject, creationProject } = useLoaderData() as LoadResult;
    const errorMsg = useActionData() as any;
    const submit = useSubmit();
    const navigate = useNavigate();

    return (
        <Modal open={true} size="large">
            <Modal.Header>Kompensation hinzufügen</Modal.Header>
            <Modal.Content scrolling={true}>
                <Table compact="very" size="small">
                    <TableHeader>
                        <TableRow verticalAlign="top">
                            <Table.HeaderCell>{getLabel(CreationProject.prototype, "id")}</Table.HeaderCell>
                            <Table.HeaderCell>{getLabel(CreationProject.prototype, "municipalityName")}</Table.HeaderCell>
                            <Table.HeaderCell>{getLabel(CreationProject.prototype, "fullType")}</Table.HeaderCell>
                            <Table.HeaderCell>{getLabel(CreationProject.prototype, "ebageNr")}</Table.HeaderCell>
                            <Table.HeaderCell>{getLabel(CreationProject.prototype, "name")}</Table.HeaderCell>
                            <Table.HeaderCell>{getLabel(CreationProject.prototype, "status")}</Table.HeaderCell>
                            <Table.HeaderCell>{getLabel(CreationProject.prototype, "enactmentDateDisplayText")}</Table.HeaderCell>
                            <Table.HeaderCell textAlign="right">Restfläche [m²]</Table.HeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow>
                            <TableCell>{creationProject.id}</TableCell>
                            <TableCell>{creationProject.municipalityName}</TableCell>
                            <TableCell>{creationProject.type}</TableCell>
                            <Table.Cell>{creationProject.ebageNr}</Table.Cell>
                            <TableCell>{creationProject.name}</TableCell>
                            <TableCell>{creationProject.status}</TableCell>
                            <TableCell>{creationProject.enactmentDateDisplayText}</TableCell>
                            <TableCell textAlign="right">{creationProject.totalAreaAvailable.toLocaleString()}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>

                <Form method="post" id="newCompensationForm">
                    <div className="compensationNew">
                        <LabeledValue label="Zu kompensierende Restfläche [m²]"
                                      value={usageProject.totalAreaToCompensate}/>
                        <LabeledInput label="Kompensationsfläche [m²]" inputElement={<Input type="number" name="area"
                                                                                       defaultValue={0}/>}/>
                        <LabeledTextArea label="Bemerkungen" name="remarks"/>
                        {errorMsg !== undefined && <Message negative>{errorMsg}</Message>}
                        <input type="hidden" name="maxArea"
                               value={creationProject.totalAreaAvailable}/>
                        <input type="hidden" name="fffUsageProjectId"
                               value={usageProject.id}/>
                        <input type="hidden" name="fffCreationProjectId"
                               value={creationProject.id}/>
                    </div>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Link to={`/projekte/${Constants.projectCategoryUsage}/${usageProject.id}`}
                      preventScrollReset={true}><Button
                    type="button">Abbrechen</Button></Link>
                <Button onClick={() => navigate(-1)}>Zurück</Button>
                <Button
                    onClick={() => submit(document.getElementById("newCompensationForm") as HTMLFormElement)}>Speichern</Button>
            </Modal.Actions>
        </Modal>
    );
}